/**
 * A sender/client NAME -> the 1-2 letters an avatar circle shows.
 *
 * Single owner for both inbox tabs (RfqListItem, InboxEmailListItem): each kept its own private
 * copy of this pair, and the two drifted apart. RfqListItem's already special-cased a single
 * word ("AppSumo" -> "AP"); InboxEmailListItem's did not, so a single-word sender there always
 * fell through to the one-letter-plus-literal-"?" branch ("AppSumo" -> "A?") — QA retest item 40,
 * 2026-10-09. There is now one copy, matching the already-correct behaviour, and both components
 * import it rather than keep their own.
 */

/** Strips a raw "Name <email>" mail header down to just the display name. */
export function cleanSenderName(name?: string): string {
  if (!name) return '';
  const withoutEmail = name.split('<')[0];
  return withoutEmail.replace(/["'<>,]/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Two letters for an avatar circle: first letter of the first word plus first letter of the
 * last word for a multi-word name, or the first two letters of a single word. '??' for nothing
 * readable as a letter at all (blank, or punctuation/digits only).
 */
export function initialsForName(name?: string): string {
  const cleaned = cleanSenderName(name);
  if (!cleaned) return '??';

  const parts = cleaned.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    const a = (parts[0][0] ?? '').toUpperCase();
    const b = (parts[parts.length - 1][0] ?? '').toUpperCase();
    const letters = (a + b).replace(/[^A-Z]/g, '');
    if (letters.length >= 2) return letters;
    if (letters.length === 1) return letters + (parts[0][1] ?? '').toUpperCase();
  }

  // One word (or a multi-word name whose letters above did not yield even one): its own first
  // two letters, never a single real letter padded with a literal "?".
  const word = parts[0].toUpperCase().replace(/[^A-Z]/g, '');
  if (word.length >= 2) return word.slice(0, 2);
  if (word.length === 1) return word;
  return '??';
}
